import React, { useState } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Box,
  LinearProgress,
  TextField,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";

/* ------------------------------ STATUS CONFIG ------------------------------ */

const STATUS_CONFIG = {
  "en attente": { color: "#FFA500" },
  "en cours de reparation": { color: "#1E90FF" },
  reparer: { color: "#32CD32" },
  retour: { color: "#800080" },
  livré: { color: "#008000" },
  "non réparable": { color: "#FF0000" },
  "aucun defaut TEST OK": { color: "#00BFA5" },
};

/* ------------------------------ HELPERS ------------------------------ */

const isInCurrentMonth = (date) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return (
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
};

const diffHours = (start, end) => {
  if (!start) return 0;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  return Math.max(0, (e - s) / (1000 * 3600));
};

const formatDuration = (hours) => {
  if (hours < 24) return `${hours.toFixed(1)} h`;
  const d = Math.floor(hours / 24);
  const h = Math.round(hours % 24);
  return `${d}j ${h}h`;
};

const isBetweenDates = (date, start, end) => {
  const d = new Date(date);
  if (start && d < new Date(start)) return false;
  if (end && d > new Date(end)) return false;
  return true;
};

const buildStatsByOperateur = (
  maintenances,
  isAdmin,
  dateDebut,
  dateFin
) => {
  const stats = {};

  maintenances
    .filter((m) => {
      if (!m.datetime_debut) return false;

      if (isAdmin && (dateDebut || dateFin)) {
        return isBetweenDates(m.datetime_debut, dateDebut, dateFin);
      }

      return isInCurrentMonth(m.datetime_debut);
    })
    .forEach((m) => {
      const op = m.operateur || "Non assigné";

      if (!stats[op]) {
        stats[op] = { total: 0 };
        Object.keys(STATUS_CONFIG).forEach((s) => {
          stats[op][s] = { count: 0, hours: 0 };
        });
      }

      stats[op].total += 1;

      if (stats[op][m.statut]) {
        stats[op][m.statut].count += 1;
        stats[op][m.statut].hours += diffHours(
          m.datetime_debut,
          m.datetime_fin
        );
      }
    });

  return stats;
};

/* ------------------------------ COMPONENT ------------------------------ */

const StatsPage = () => {
  const maintenances = useSelector((s) => s.maintenances.items);
  const _userRole = useSelector((s) => s.session.user?.role);

  const isAdmin =
    _userRole === "admin" || _userRole === "superadmin";

  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  const statsByOperateur = buildStatsByOperateur(
    maintenances,
    isAdmin,
    dateDebut,
    dateFin
  );

  return (
    <>
      {/* TITLE */}
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 3,
          fontWeight: 900,
          background: "linear-gradient(90deg,#002355,#00b0ff)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
         Dashboard Maintenance
      </Typography>

      {/* DATE FILTERS (ADMIN ONLY) */}
      {isAdmin && (
        <Paper
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 3,
            boxShadow: 3,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Date début"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
            />
            <TextField
              label="Date fin"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
            />
          </Stack>
        </Paper>
      )}

      {/* CARDS */}
      <Grid container spacing={3}>
        {Object.entries(statsByOperateur).map(([op, data]) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={op}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                background:
                  "linear-gradient(180deg,#ffffff,#f4f7fb)",
                boxShadow: "0 10px 35px rgba(0,0,0,.15)",
                transition: "0.35s",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 18px 55px rgba(0,0,0,.25)",
                },
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={800}>
                  👨‍🔧 {op}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Total opérations : <b>{data.total}</b>
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.5}>
                  {Object.entries(STATUS_CONFIG).map(
                    ([status, cfg]) => {
                      const { count, hours } = data[status];
                      const percent = data.total
                        ? Math.round(
                            (count / data.total) * 100
                          )
                        : 0;

                      return (
                        <Box key={status}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2">
                              {status}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                            >
                              {count} | {formatDuration(hours)}
                            </Typography>
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                              height: 8,
                              borderRadius: 5,
                              backgroundColor: "#e0e0e0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: cfg.color,
                              },
                            }}
                          />
                        </Box>
                      );
                    }
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* EMPTY */}
      {Object.keys(statsByOperateur).length === 0 && (
        <Typography align="center" mt={6} color="text.secondary">
          Aucune donnée pour la période sélectionnée
        </Typography>
      )}
    </>
  );
};

export default StatsPage;
